import { useEffect, useState } from 'react'
import { useAppStore } from '../../store/app'
import { bridge } from '../../bridge'
import { logout } from '../../api/logout'
import { NavBar } from '../../components/NavBar'
import { SectionHeader, Group, Row, TappableRow } from './AccountComponents'
import './AccountStyles.css'
import './AccountScreen.css'

const AUTO_SEND_KEY = 'whisperAutoSend'

export function AccountScreen() {
  const email = useAppStore((s) => s.email) ?? ''
  const organizations = useAppStore((s) => s.availableOrganizations)
  const ownedOrgCap = useAppStore((s) => s.ownedOrgCap)
  const myInvites = useAppStore((s) => s.myInvites)
  const connectionStatus = useAppStore((s) => s.connectionStatus)
  const pushSheet = useAppStore((s) => s.pushSheet)
  // Entitlement enforcement is disconnected — there is no paywall and no Pro
  // tier. The whole Subscription section (plan badge, upgrade rows, Restore)
  // and its StoreKit-derived state were removed. See
  // docs/entitlements-disconnected.md.

  const [version, setVersion] = useState('—')
  const [whisperAvailable, setWhisperAvailable] = useState(false)
  const [autoSend, setAutoSend] = useState(
    () => localStorage.getItem(AUTO_SEND_KEY) !== 'false',
  )

  useEffect(() => {
    bridge
      .call('app.info')
      .then((info) => setVersion(`${info.version} (${info.build})`))
      .catch(() => setVersion('—'))
    bridge
      .call('whisper.isAvailable')
      .then((r) => setWhisperAvailable(r.supported))
      .catch(() => setWhisperAvailable(false))
  }, [])

  useEffect(() => {
    localStorage.setItem(AUTO_SEND_KEY, autoSend ? 'true' : 'false')
  }, [autoSend])

  const connColor =
    connectionStatus === 'connected'
      ? 'var(--success)'
      : connectionStatus === 'connecting'
        ? 'var(--warning)'
        : 'var(--danger)'
  const connLabel =
    connectionStatus === 'connected'
      ? 'Connected'
      : connectionStatus === 'connecting'
        ? 'Connecting…'
        : 'Disconnected'

  return (
    <div className="account-screen">
      <NavBar title="Account" />

      <div className="account-screen__scroll">
        <SectionHeader>
          Connection
          {import.meta.env.VITE_DEV_DEPLOY && (
            <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 600, color: '#fff', background: '#e05c2a', borderRadius: 4, padding: '1px 5px', position: 'relative', top: -2 }}>DEV</span>
          )}
        </SectionHeader>
        <Group>
          <Row label="Status">
            <span className="about__status">
              <span className="about__status-dot" style={{ background: connColor }} />
              <span className="about__status-text">{connLabel}</span>
            </span>
          </Row>
        </Group>

        <SectionHeader>Account</SectionHeader>
        <Group>
          <Row label="Email">
            <span className="about__detail">{email || '—'}</span>
          </Row>
          <button
            className="about__row about__row--destructive"
            onClick={() => {
              if (confirm('Log out of Hearth on this device?')) {
                void logout()
              }
            }}
          >
            <span className="about__row-label">Log Out</span>
          </button>
        </Group>

        <SectionHeader>Households</SectionHeader>
        <Group>
          {(() => {
            const ownedCount = organizations.filter((o) => o.role === 'owner').length
            const atCap = ownedOrgCap > 0 && ownedCount >= ownedOrgCap
            if (atCap) {
              return (
                <Row label="Create New Household">
                  <span className="about__row-value about__row-value--muted">
                    {ownedCount}/{ownedOrgCap} limit reached
                  </span>
                </Row>
              )
            }
            return (
              <TappableRow
                label="Create New Household"
                onClick={() => pushSheet({ kind: 'household_picker', initialMode: 'create' })}
              >
                <span className="about__chevron">›</span>
              </TappableRow>
            )
          })()}
          <TappableRow
            label="Invitations"
            onClick={() => pushSheet({ kind: 'invitations' })}
          >
            {myInvites.length > 0 && (
              <span className="about__row-value">{myInvites.length}</span>
            )}
            <span className="about__chevron">›</span>
          </TappableRow>
          <TappableRow
            label="Delegate Approval"
            onClick={() => pushSheet({ kind: 'delegations' })}
          >
            <span className="about__chevron">›</span>
          </TappableRow>
        </Group>

        <SectionHeader>Notifications</SectionHeader>
        <Group>
          <TappableRow
            label="Notification Preferences"
            onClick={() => pushSheet({ kind: 'notification_preferences' })}
          >
            <span className="about__chevron">›</span>
          </TappableRow>
          <TappableRow
            label="Open Requests"
            onClick={() => pushSheet({ kind: 'open_requests' })}
          >
            <span className="about__chevron">›</span>
          </TappableRow>
          {whisperAvailable && (
            <div className="about__row">
              <span className="about__row-label">Auto-send after recording</span>
              <label className="about__toggle">
                <input
                  type="checkbox"
                  checked={autoSend}
                  onChange={(e) => setAutoSend(e.target.checked)}
                />
                <span className="about__toggle-track" />
              </label>
            </div>
          )}
          {!bridge.isNative && <BrowserNotificationsSection />}
        </Group>

        {import.meta.env.VITE_DEV_DEPLOY && (
          <>
            <SectionHeader>Developer</SectionHeader>
            <Group>
              <TappableRow
                label="Developer Area"
                onClick={() => pushSheet({ kind: 'developer' })}
              >
                <span className="about__chevron">›</span>
              </TappableRow>
            </Group>
          </>
        )}

        {bridge.isNative && (
          <>
            <SectionHeader>About</SectionHeader>
            <Group>
              <Row label="Version">
                <span className="about__detail">{version}</span>
              </Row>
            </Group>
          </>
        )}

        <SectionHeader>Legal</SectionHeader>
        <Group>
          <a
            className="about__row about__row--link"
            href="https://hearthcmd.com/privacy.html"
            target="_blank"
            rel="noreferrer"
          >
            <span className="about__row-label">Privacy Policy</span>
            <span className="about__chevron">›</span>
          </a>
          <a
            className="about__row about__row--link"
            href="https://hearthcmd.com/terms.html"
            target="_blank"
            rel="noreferrer"
          >
            <span className="about__row-label">Terms of Service</span>
            <span className="about__chevron">›</span>
          </a>
          <button
            className="about__row about__row--tappable"
            onClick={() => pushSheet({ kind: 'delete_account' })}
          >
            <span className="about__row-label">Delete Account</span>
            <span className="about__chevron">›</span>
          </button>
        </Group>
      </div>
    </div>
  )
}

function BrowserNotificationsSection() {
  const supported =
    typeof window !== 'undefined' &&
    'Notification' in window &&
    'serviceWorker' in navigator &&
    'PushManager' in window

  const [permission, setPermission] = useState<NotificationPermission | 'unsupported'>(
    supported ? Notification.permission : 'unsupported',
  )
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function enable() {
    setBusy(true)
    setError(null)
    try {
      const r = await bridge.call('apns.registerForPush')
      if (supported) setPermission(Notification.permission)
      if (!r.authorized) {
        setError(
          'Subscription failed. Check that notifications are allowed for this site in your browser settings.',
        )
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  if (!supported) return null

  const statusLabel =
    permission === 'granted' ? 'Enabled'
    : permission === 'denied' ? 'Blocked by browser'
    : 'Not enabled'
  const dotColor =
    permission === 'granted' ? 'var(--success)'
    : permission === 'denied' ? 'var(--danger)'
    : '#888'

  return (
    <>
      <Row label="Browser Notifications">
        <span className="about__status">
          <span className="about__status-dot" style={{ background: dotColor }} />
          <span className="about__status-text">{statusLabel}</span>
        </span>
      </Row>
      {permission !== 'granted' && permission !== 'denied' && (
        <button
          className="about__row about__row--tappable"
          disabled={busy}
          onClick={enable}
        >
          <span className="about__row-label">
            {busy ? 'Enabling…' : 'Enable browser notifications'}
          </span>
          <span className="about__chevron">›</span>
        </button>
      )}
      {permission === 'granted' && (
        <button
          className="about__row about__row--tappable"
          disabled={busy}
          onClick={enable}
        >
          <span className="about__row-label">
            {busy ? 'Re-subscribing…' : 'Re-subscribe (refresh server token)'}
          </span>
          <span className="about__chevron">›</span>
        </button>
      )}
      {permission === 'denied' && (
        <Row label="Note">
          <span className="about__detail">
            Notifications are blocked at the browser level. Re-enable in
            your browser's site settings and reload.
          </span>
        </Row>
      )}
      {error && (
        <Row label="Error">
          <span className="about__detail">{error}</span>
        </Row>
      )}
    </>
  )
}
